import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Check, ChevronLeft, ChevronRight, X } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { statusChipClass, statusLabel } from "@/lib/mapping-status-colors";
import { cn } from "@/lib/utils";
import { LockerList, type LockerRow } from "./LockerList";
import { TierSelector } from "./TierSelector";

type PilRole = "main" | "backup" | "none";

export interface PILForReview {
  id: string;
  ordinal: number;
  status: string;
  is_viable: boolean | null;
  general_comments: string | null;
  admin_notes: string | null;
  admin_tier: number | null;
  submitted_at: string | null;
  rejection_reason: string | null;
  role: PilRole | null;
  lockers: LockerRow[];
}

export interface LocationMappingSummary {
  public_access: boolean | null;
  open_24_7: boolean | null;
  opening_hours: string | null;
  passing_by_visibility: number | null;
  nearby_habitations: number | null;
  signs_of_degradation: boolean | null;
  janitors: boolean | null;
  lighting: boolean | null;
  dubious_people: boolean | null;
  video_cameras: boolean | null;
}

interface PILReviewFormProps {
  locationId: string;
  collectionId: string;
  pil: PILForReview;
  locationSports: string[];
  locationSummary: LocationMappingSummary;
  prevPilId: string | null;
  nextPilId: string | null;
  onAdvanceAfterDecision: () => void;
  onNavigate: (pilId: string) => void;
}

type Tier = 1 | 2 | 3;

function YesNo({ value }: { value: boolean | null }) {
  if (value === null) return <span className="text-muted-foreground">–</span>;
  return value ? (
    <span className="inline-flex items-center gap-1 text-emerald-700">
      <Check className="size-3.5" />
      Yes
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-rose-700">
      <X className="size-3.5" />
      No
    </span>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return "–";
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export function PILReviewForm({
  locationId,
  collectionId,
  pil,
  locationSports,
  locationSummary,
  prevPilId,
  nextPilId,
  onAdvanceAfterDecision,
  onNavigate,
}: PILReviewFormProps) {
  const utils = trpc.useUtils();
  const [adminNotes, setAdminNotes] = useState(pil.admin_notes ?? "");

  useEffect(() => {
    setAdminNotes(pil.admin_notes ?? "");
  }, [pil.id, pil.admin_notes]);

  const invalidate = () =>
    Promise.all([
      utils.physicalMapping.listPILsForReview.invalidate({
        collectionId,
        locationId,
      }),
      utils.physicalMapping.getLocationDetails.invalidate({ locationId }),
      utils.physicalMapping.list.invalidate(),
    ]);

  const updatePIL = trpc.physicalMapping.updatePIL.useMutation({
    onSuccess: () => invalidate(),
    onError: (e) => toast.error(e.message),
  });

  const setLocker = trpc.physicalMapping.setLocker.useMutation({
    onSuccess: () => invalidate(),
    onError: (e) => toast.error(e.message),
  });

  const deleteLocker = trpc.physicalMapping.deleteLocker.useMutation({
    onSuccess: () => invalidate(),
    onError: (e) => toast.error(e.message),
  });

  const setAdminTier = trpc.physicalMapping.setAdminTier.useMutation({
    onSuccess: () => invalidate(),
    onError: (e) => toast.error(e.message),
  });

  const approve = trpc.physicalMapping.approvePIL.useMutation({
    onSuccess: async () => {
      await invalidate();
      toast.success(`PIL #${pil.ordinal} approved`);
      onAdvanceAfterDecision();
    },
    onError: (e) => toast.error(e.message),
  });

  const reopen = trpc.physicalMapping.reopenPIL.useMutation({
    onSuccess: () => invalidate(),
    onError: (e) => toast.error(e.message),
  });

  const reject = trpc.physicalMapping.rejectPIL.useMutation({
    onSuccess: async (_data, variables) => {
      await invalidate();
      const rejectedPilId = variables.pilId;
      toast.success("Spot rejected", {
        action: {
          label: "Undo",
          onClick: () => reopen.mutate({ pilId: rejectedPilId }),
        },
      });
      onAdvanceAfterDecision();
    },
    onError: (e) => toast.error(e.message),
  });

  const setRole = trpc.physicalMapping.setPilRole.useMutation({
    onSuccess: async () => {
      await Promise.all([
        invalidate(),
        utils.physicalMapping.getCityReviewSummary.invalidate({ collectionId }),
      ]);
    },
    onError: (e) => toast.error(e.message),
  });

  const allLockersHaveSport = useMemo(() => {
    if (pil.lockers.length === 0) return false;
    return pil.lockers.every((l) => l.sport_type && l.sport_type.trim() !== "");
  }, [pil.lockers]);

  const canApprove =
    pil.admin_tier != null && allLockersHaveSport && !approve.isPending;
  const isMutating =
    updatePIL.isPending ||
    setLocker.isPending ||
    deleteLocker.isPending ||
    setAdminTier.isPending ||
    approve.isPending ||
    reject.isPending;

  const showWarning = pil.status !== "done";

  return (
    <div className="flex flex-col h-full">
      {/* Header: ordinal + nav */}
      <div className="flex items-center gap-2 px-4 py-2 border-b shrink-0">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          disabled={!prevPilId}
          onClick={() => prevPilId && onNavigate(prevPilId)}
          aria-label="Previous PIL"
        >
          <ChevronLeft className="size-4" />
        </Button>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className="text-sm font-semibold">PIL #{pil.ordinal}</span>
          <Badge variant="outline" className={cn("text-xs", statusChipClass(pil.status))}>
            {statusLabel(pil.status)}
          </Badge>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          disabled={!nextPilId}
          onClick={() => nextPilId && onNavigate(nextPilId)}
          aria-label="Next PIL"
        >
          <ChevronRight className="size-4" />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {showWarning && (
          <Alert className="border-amber-300 bg-amber-50 text-amber-900">
            <AlertTriangle className="size-4 !text-amber-600" />
            <AlertTitle className="text-amber-900">Not yet marked done</AlertTitle>
            <AlertDescription className="text-amber-800">
              Mapper has not yet marked this PIL as done (current status:{" "}
              {statusLabel(pil.status)}).
            </AlertDescription>
          </Alert>
        )}

        {/* Mapper fields — read-only */}
        <section className="space-y-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Mapper submission
          </h3>
          <div className="rounded-lg border bg-muted/30 p-3 text-sm space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="text-xs text-muted-foreground">Submitted</div>
                <div>{formatDate(pil.submitted_at)}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Viable?</div>
                <YesNo value={pil.is_viable} />
              </div>
            </div>
            {pil.general_comments && (
              <div>
                <div className="text-xs text-muted-foreground">Mapper comments</div>
                <div className="whitespace-pre-wrap text-sm">{pil.general_comments}</div>
              </div>
            )}
          </div>
        </section>

        {/* Location mapping answers — read-only */}
        <section className="space-y-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Location context
          </h3>
          <div className="rounded-lg border bg-muted/30 p-3 text-sm">
            <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5">
              <dt className="text-xs text-muted-foreground">Public access</dt>
              <dd><YesNo value={locationSummary.public_access} /></dd>

              <dt className="text-xs text-muted-foreground">Open 24/7</dt>
              <dd><YesNo value={locationSummary.open_24_7} /></dd>

              <dt className="text-xs text-muted-foreground">Opening hours</dt>
              <dd className="truncate">{locationSummary.opening_hours ?? "–"}</dd>

              <dt className="text-xs text-muted-foreground">Visibility</dt>
              <dd>{locationSummary.passing_by_visibility ?? "–"}/5</dd>

              <dt className="text-xs text-muted-foreground">Nearby habitations</dt>
              <dd>{locationSummary.nearby_habitations ?? "–"}/5</dd>

              <dt className="text-xs text-muted-foreground">Degradation</dt>
              <dd><YesNo value={locationSummary.signs_of_degradation} /></dd>

              <dt className="text-xs text-muted-foreground">Janitors</dt>
              <dd><YesNo value={locationSummary.janitors} /></dd>

              <dt className="text-xs text-muted-foreground">Lighting</dt>
              <dd><YesNo value={locationSummary.lighting} /></dd>

              <dt className="text-xs text-muted-foreground">Dubious people</dt>
              <dd><YesNo value={locationSummary.dubious_people} /></dd>

              <dt className="text-xs text-muted-foreground">Video cameras</dt>
              <dd><YesNo value={locationSummary.video_cameras} /></dd>
            </dl>
          </div>
        </section>

        {/* Lockers */}
        <section className="space-y-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Lockers
          </h3>
          <LockerList
            lockers={pil.lockers}
            locationSports={locationSports}
            onSetSport={(lockerIndex, sport) =>
              setLocker.mutate({ pilId: pil.id, lockerIndex, sportType: sport })
            }
            onDeleteLocker={(lockerIndex) =>
              deleteLocker.mutate({ pilId: pil.id, lockerIndex })
            }
            disabled={isMutating}
          />
          {!allLockersHaveSport && (
            <p className="text-xs text-muted-foreground">
              Every remaining locker must have a sport before this PIL can be approved.
            </p>
          )}
        </section>

        {/* Tier */}
        <section className="space-y-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Tier
          </h3>
          <TierSelector
            value={(pil.admin_tier as Tier | null) ?? null}
            onChange={(tier) => setAdminTier.mutate({ pilId: pil.id, tier })}
            disabled={isMutating}
          />
        </section>

        {/* Station role — main / backup / none */}
        <section className="space-y-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Station role
          </h3>
          <ToggleGroup
            type="single"
            variant="outline"
            value={pil.role ?? "none"}
            onValueChange={(v) => {
              if (!v) return;
              setRole.mutate({
                pilId: pil.id,
                role: v as PilRole,
              });
            }}
            disabled={setRole.isPending || isMutating}
          >
            <ToggleGroupItem value="main" aria-label="Main station">
              Main
            </ToggleGroupItem>
            <ToggleGroupItem value="backup" aria-label="Backup station">
              Backup
            </ToggleGroupItem>
            <ToggleGroupItem value="none" aria-label="Not included">
              None
            </ToggleGroupItem>
          </ToggleGroup>
          <p className="text-[11px] text-muted-foreground">
            Main stations count toward the hub; backups are shown to the city
            as alternatives. Takes effect once the PIL is approved.
          </p>
        </section>

        {/* Admin notes */}
        <section className="space-y-2">
          <Label htmlFor="admin-notes" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Admin notes
          </Label>
          <Textarea
            id="admin-notes"
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            onBlur={() => {
              if (adminNotes !== (pil.admin_notes ?? "")) {
                updatePIL.mutate({
                  id: pil.id,
                  patch: { admin_notes: adminNotes || null },
                });
              }
            }}
            placeholder="Internal notes (not shown to mapper)…"
            rows={3}
          />
        </section>

        {pil.rejection_reason && (
          <section className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm">
            <div className="text-xs font-semibold text-rose-800 uppercase tracking-wide mb-1">
              Rejection reason
            </div>
            <div className="text-rose-900 whitespace-pre-wrap">{pil.rejection_reason}</div>
          </section>
        )}
      </div>

      {/* Footer actions */}
      <div className="border-t p-3 flex items-center gap-2 shrink-0 bg-background">
        <Button
          variant="outline"
          className="border-rose-300 text-rose-700 hover:bg-rose-50 hover:text-rose-800"
          disabled={isMutating}
          onClick={() => reject.mutate({ pilId: pil.id, reason: "" })}
        >
          Reject
        </Button>
        <div className="flex-1" />
        <Button
          disabled={!canApprove}
          onClick={() => approve.mutate({ pilId: pil.id })}
          title={
            !canApprove
              ? "Set tier and assign a sport to every locker first."
              : "Approve this PIL"
          }
        >
          <Check className="size-4 mr-1" />
          Approve
        </Button>
      </div>

    </div>
  );
}
